import React, { useState, useEffect } from 'react';

function ExpandIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Wraps any chart card. Children must be a render function: (height) => <JSX>
 * Props:
 *   title           – card heading
 *   subtitle        – optional sub-text below the heading
 *   normalHeight    – chart height in card (default 340)
 *   fullscreenHeight – chart height inside the modal (default max(normalHeight*1.8, 520))
 *   extraContent    – optional JSX rendered after the chart (both in card and modal)
 */
export default function ExpandableChartCard({
  title,
  subtitle,
  children,
  normalHeight = 340,
  fullscreenHeight,
  extraContent,
  headerExtra,
}) {
  const [expanded, setExpanded] = useState(false);
  const modalHeight =
    fullscreenHeight != null
      ? fullscreenHeight
      : Math.max(Math.round(normalHeight * 1.8), 520);

  useEffect(() => {
    if (!expanded) return;
    function handleKey(e) {
      if (e.key === 'Escape') setExpanded(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [expanded]);

  const renderChart = (height) =>
    typeof children === 'function' ? children(height) : children;

  return (
    <>
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {subtitle && <div className="text-sm text-gray-600 mt-1">{subtitle}</div>}
          </div>
          <button
            onClick={() => setExpanded(true)}
            className="ml-3 shrink-0 p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            title="Agrandir le graphique"
            aria-label="Agrandir le graphique"
          >
            <ExpandIcon />
          </button>
        </div>
        {headerExtra && <div className="mt-3">{headerExtra}</div>}
        <div className="mt-4">{renderChart(normalHeight)}</div>
        {extraContent}
      </div>

      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setExpanded(false)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-[95vw] flex flex-col shadow-2xl overflow-hidden"
            style={{ height: '92vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                {subtitle && <div className="text-sm text-gray-600 mt-1">{subtitle}</div>}
              </div>
              <button
                onClick={() => setExpanded(false)}
                className="ml-4 p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                title="Fermer"
                aria-label="Fermer"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
              {headerExtra && <div className="mb-4">{headerExtra}</div>}
              {renderChart(modalHeight)}
              {extraContent}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
